"use server";

import { apiFetchServer } from "@/src/lib/api/fetch.server";
import type { KnifeJson } from "@/src/types/diecut";
import type {
  AnimationConfig,
  CustomFoldLine,
  CustomPanel,
  ParsedSvgDiecut,
} from "@/src/types/diecut-configurator.types";
import { svgToKnifeJson } from "@/src/utils/svgToKnifeJson";

function deriveCanvasTotals(panels: CustomPanel[]): { totalX: number; totalY: number } {
  let maxX = 0;
  let maxY = 0;
  for (const p of panels) {
    const b = p.geometry.bounds;
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  return { totalX: maxX || 100, totalY: maxY || 100 };
}

export interface DiecutListItem {
  id: string;
  slug: string;
  name: string | null;
  categoryTitle: string | null;
  thumbnailPath: string | null;
  thumbnailUrl: string | null;
  isCustomDimensions: boolean;
  isFeatured: boolean;
  isCustom: boolean;
  length: string | null;
  width: string | null;
  height: string | null;
  dimensionUnit: "in" | "mm" | "cm";
  default3dRotW: number | null;
  default3dRotX: number | null;
  default3dRotY: number | null;
  default3dRotZ: number | null;
  default3dDistance: number | null;
  defaultArRotY: number | null;
  defaultArFace: string | null;
  complexity?: "simple" | "medium" | "complex" | null;
  tags?: string[] | null;
  description?: string | null;
}

export interface DiecutDetail {
  id: string;
  slug: string;
  name: string | null;
  categoryTitle: string | null;
  thumbnailPath: string | null;
  thumbnailUrl: string | null;
  length: string | null;
  width: string | null;
  height: string | null;
  dimensionUnit: "in" | "mm" | "cm";
  complexity: "simple" | "medium" | "complex" | null;
  isFeatured: boolean;
  isCustomDimensions: boolean;
  tags: string[] | null;
  description: string | null;
  knifeDataPath: string | null;
  knifeDataUrl: string | null;
}

type BackendDiecut = {
  id: string;
  slug: string;
  name?: string | null;
  categoryTitle?: string | null;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  knifeDataPath?: string | null;
  knifeDataUrl?: string | null;
  isCustomDimensions: boolean;
  isFeatured: boolean;
  isCustom: boolean;
  length?: string | null;
  width?: string | null;
  height?: string | null;
  dimensionUnit: "in" | "mm" | "cm";
  default3dRotW?: number | null;
  default3dRotX?: number | null;
  default3dRotY?: number | null;
  default3dRotZ?: number | null;
  default3dDistance?: number | null;
  defaultArRotY?: number | null;
  defaultArFace?: string | null;
  complexity?: "simple" | "medium" | "complex" | null;
  tags?: string | null;
  description?: string | null;
  customPanels?: CustomPanel[] | null;
  customFoldLines?: CustomFoldLine[] | null;
  animationData?: AnimationConfig | null;
};

type PaginatedDiecuts = {
  data: BackendDiecut[];
  total: number;
  page: number;
  limit: number;
};

export interface DiecutCategorySummary {
  slug: string;
  name: string;
  count: number;
}

export interface DiecutListPageParams {
  page?: number;
  limit?: number;
  active?: boolean;
  categorySlug?: string;
  search?: string;
}

export interface DiecutListPageResult {
  items: DiecutListItem[];
  total: number;
  page: number;
  limit: number;
}

function parseTags(tags: string | null | undefined): string[] | null {
  if (!tags) return null;
  try {
    const parsed = JSON.parse(tags) as string[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function mapBackendToListItem(row: BackendDiecut): DiecutListItem {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name ?? null,
    categoryTitle: row.categoryTitle ?? null,
    thumbnailPath: row.thumbnailPath ?? null,
    thumbnailUrl: row.thumbnailUrl ?? null,
    isCustomDimensions: row.isCustomDimensions,
    isFeatured: row.isFeatured,
    isCustom: row.isCustom,
    length: row.length != null ? String(row.length) : null,
    width: row.width != null ? String(row.width) : null,
    height: row.height != null ? String(row.height) : null,
    dimensionUnit: row.dimensionUnit,
    default3dRotW: row.default3dRotW ?? null,
    default3dRotX: row.default3dRotX ?? null,
    default3dRotY: row.default3dRotY ?? null,
    default3dRotZ: row.default3dRotZ ?? null,
    default3dDistance: row.default3dDistance ?? null,
    defaultArRotY: row.defaultArRotY ?? null,
    defaultArFace: row.defaultArFace ?? null,
    complexity: row.complexity ?? null,
    tags: parseTags(row.tags),
    description: row.description ?? null,
  };
}

function buildDiecutListQuery(params: DiecutListPageParams): string {
  const qs = new URLSearchParams();
  if (params.active !== undefined) qs.set("active", String(params.active));
  if (params.categorySlug) qs.set("categorySlug", params.categorySlug);
  if (params.search?.trim()) qs.set("search", params.search.trim());
  qs.set("page", String(params.page ?? 1));
  qs.set("limit", String(params.limit ?? 50));
  return qs.toString();
}

export async function listDiecutsPage(
  params: DiecutListPageParams = {},
): Promise<DiecutListPageResult> {
  const res = await apiFetchServer<PaginatedDiecuts>(
    `/diecuts?${buildDiecutListQuery(params)}`,
    { skipAuth: true },
  );
  return {
    items: res.data.map(mapBackendToListItem),
    total: res.total,
    page: res.page,
    limit: res.limit,
  };
}

export async function getDiecutCategorySummary(): Promise<DiecutCategorySummary[]> {
  return apiFetchServer<DiecutCategorySummary[]>("/diecuts/categories/summary", {
    skipAuth: true,
  });
}

export async function resolveDiecutListItem(slug: string): Promise<DiecutListItem | null> {
  try {
    const row = await apiFetchServer<BackendDiecut>(`/diecuts/${slug}`, {
      skipAuth: true,
    });
    return mapBackendToListItem(row);
  } catch {
    return null;
  }
}

export async function resolveDiecutListItemById(id: string): Promise<DiecutListItem | null> {
  try {
    const row = await apiFetchServer<BackendDiecut>(`/diecuts/id/${id}`, {
      skipAuth: true,
    });
    return mapBackendToListItem(row);
  } catch {
    return null;
  }
}

export async function getDiecuts(): Promise<DiecutListItem[]> {
  const res = await listDiecutsPage({ active: true, limit: 100, page: 1 });
  return res.items;
}

export async function getDiecutBySlug(slug: string): Promise<DiecutDetail | null> {
  try {
    const row = await apiFetchServer<BackendDiecut>(`/diecuts/${slug}`, {
      skipAuth: true,
    });
    return {
      id: row.id,
      slug: row.slug,
      name: row.name ?? null,
      categoryTitle: row.categoryTitle ?? null,
      thumbnailPath: row.thumbnailPath ?? null,
      thumbnailUrl: row.thumbnailUrl ?? null,
      length: row.length != null ? String(row.length) : null,
      width: row.width != null ? String(row.width) : null,
      height: row.height != null ? String(row.height) : null,
      dimensionUnit: row.dimensionUnit,
      complexity: row.complexity ?? null,
      isFeatured: row.isFeatured,
      isCustomDimensions: row.isCustomDimensions,
      tags: parseTags(row.tags),
      description: row.description ?? null,
      knifeDataPath: row.knifeDataPath ?? null,
      knifeDataUrl: row.knifeDataUrl ?? null,
    };
  } catch {
    return null;
  }
}

export async function getDiecutKnifeData(
  slug: string,
): Promise<KnifeJson | null> {
  try {
    const meta = await apiFetchServer<{
      knifeDataUrl: string | null;
      isCustom: boolean;
      customPanels: CustomPanel[] | null;
      customFoldLines: CustomFoldLine[] | null;
    }>(`/diecuts/${slug}/knife`, { skipAuth: true });

    if (meta.isCustom) {
      const panels = meta.customPanels ?? [];
      if (panels.length === 0) return null;
      const foldLines = meta.customFoldLines ?? [];
      const { totalX, totalY } = deriveCanvasTotals(panels);
      return svgToKnifeJson({
        panels,
        foldLines,
        totalX,
        totalY,
      } as ParsedSvgDiecut);
    }

    if (!meta.knifeDataUrl) return null;
    const res = await fetch(meta.knifeDataUrl, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as KnifeJson;
  } catch {
    return null;
  }
}

export interface DiecutCustomData {
  animationData: AnimationConfig | null;
}

export async function getDiecutCustomData(
  slug: string,
): Promise<DiecutCustomData | null> {
  try {
    const meta = await apiFetchServer<{
      isCustom: boolean;
      animationData: AnimationConfig | null;
    }>(`/diecuts/${slug}/knife`, { skipAuth: true });
    if (!meta.isCustom) return null;
    return { animationData: meta.animationData ?? null };
  } catch {
    return null;
  }
}
